import React, { FC, useCallback } from 'react';
import { Image, View } from 'react-native';
import { connect } from 'react-redux';
import { ProductionBatchStatistics } from '@interfaces/search';
import { useFocusEffect } from '@react-navigation/native';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { DOC_TYPE, DOCUMENT_ENTITY_TYPE, DOCUMENT_TYPE_TAG_KEY_MAP } from '@components/imageHandling/common';
import Preview from '@components/imageHandling/components/thumbnails';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { PanelProps } from '@components/operatorPanel/components/panel';
import {
  OP_DOCUMENTS_DISPLAY_MODE,
  OPERATOR_PANEL_HOME_SCREEN_BUTTON_HEIGHT,
  OPERATOR_PANEL_HOME_SCREEN_BUTTON_WIDTH,
  OPERATOR_PANEL_INFORMATION_SCREENS,
} from '@components/operatorPanel/constants/operatorPanel.constants';
import { getBoxShadowStyle } from '@styles/shadow';
import { machineStatusName, navigateTo } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import { useGetImagesByPriorities } from './hooks/useGetImagesByPriorities';
import { SmallLoading } from '@components/loading';
import { useImageHandlingViewModelWithContextUpdate } from '@v2/components/imageHandling/hooks/useImageHandlingViewModelWithContextUpdate';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import ProgressiveImage from '@v2/components/imageHandling/components/ProgressiveImage';

interface HomePageImagesProps extends Pick<PanelProps, 'CC' | 'scale' | 'font'> {
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
  C: ColorFunction;
  machineStatus: string;
  selectedMachineId: number;
}

const HomePageImages: FC<HomePageImagesProps> = ({ batch, machineStatus, CC, C, selectedMachineId, scale = initialScale, font = initialFont }) => {
  const batchId = batch?.data?.productionBatchId;

  const {
    state: { imagesDocuments: images, loading },
    actions: { loadImagesDocuments },
  } = useImageHandlingViewModelWithContextUpdate({
    imageContext: { scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.Batch], scopeReferenceId: batchId },
    retrieveAllDocuments: true,
  });

  const { selectImagesByPriority } = useGetImagesByPriorities();

  const imagesToDisplay = selectImagesByPriority(images);

  useFocusEffect(
    useCallback(() => {
      const abortController = new AbortController();

      if (batchId == null) {
        return;
      }

      loadImagesDocuments();

      return () => abortController.abort();
    }, [batchId])
  );

  const shadowStyles = getBoxShadowStyle({ type: 'Very Wide', C, scheme: 'light' });

  const borderRadius = 18;

  const onImagePress = ({ type, image }: { type: string; image: ImageDocumentStoreItem }) => {
    const targetScreenConfig = {
      [OPERATOR_PANEL_INFORMATION_SCREENS.Setup]: type.includes(DOC_TYPE.SETUP_SHEET),
      [OPERATOR_PANEL_INFORMATION_SCREENS['2DDrawing']]: type.includes(DOC_TYPE['2D_DRAWING']),
      [OPERATOR_PANEL_INFORMATION_SCREENS.Clamping]: type.includes(DOC_TYPE.CLAMPING_IMAGE),
      [OPERATOR_PANEL_INFORMATION_SCREENS.Part]: type.includes(DOC_TYPE.PART_IMAGE),
    };

    let targetScreen = null;

    for (let screen of Object.keys(targetScreenConfig)) {
      if (targetScreenConfig[screen as keyof typeof targetScreenConfig]) {
        targetScreen = screen;
        break;
      }
    }

    if (!targetScreen) return;

    const params = { selectedImageKey: image.contentHash, documentId: image.id, id: selectedMachineId, mode: OP_DOCUMENTS_DISPLAY_MODE.GALLERY };

    navigateTo(OPERATOR_PANEL_INFORMATION_SCREENS.Information, {
      screen: targetScreen,
      params,
    });
  };

  // Show loading indicator while loading
  if (loading && batchId) {
    return (
      <View
        style={{
          width: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_WIDTH),
          height: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_HEIGHT),
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <SmallLoading />
      </View>
    );
  }

  // Don't show anything if no batch or no images
  if (!batchId || images.length === 0) {
    return null;
  }

  // Check if we have images to display after priority selection
  if (imagesToDisplay.length === 0) {
    return null;
  }

  return (
    <>
      {imagesToDisplay.map((i, index) => (
        <TouchableElement onPress={() => onImagePress(i)} key={i.image.contentHash}>
          {({ boxStyle }) => (
            <View
              style={[
                boxStyle,
                {
                  width: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_WIDTH),
                  height: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_HEIGHT),
                  borderRadius: scale(borderRadius),
                  backgroundColor: CC(`Button/Light/Operator Panel [${machineStatusName[machineStatus as keyof typeof machineStatusName]}]`),
                },
                shadowStyles,
              ]}
            >
              {i.image.isDocument && (
                <Preview machineList fontName="Subhead 2" item={i.image} CC={CC} alwaysDark borderRadius={scale(borderRadius)} />
              )}
              {!i.image.isDocument && (
                <Image
                  source={{ uri: i.image?.urlSmall }}
                  style={{
                    borderRadius: scale(borderRadius),
                    width: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_WIDTH),
                    height: scale(OPERATOR_PANEL_HOME_SCREEN_BUTTON_HEIGHT),
                  }}
                  resizeMode="contain"
                />
              )}
            </View>
          )}
        </TouchableElement>
      ))}
    </>
  );
};

export default connect(
  ({ batch: { batch } }) => ({
    batch,
  }),
  {}
)(ColorWrapper(HomePageImages));
